import type { FC, ReactNode } from 'react'

import { Quote } from 'lucide-react'
import { cn, getInitials } from '@/lib/utils'
import Container from '@/components/landing/container'
import SectionHeading from '@/components/landing/section-heading'
import StarRating from '@/components/landing/star-rating'

import { TESTIMONIALS } from '@/constants/landing'

const AVATAR_TONES: string[] = [
    'bg-secondary text-primary',
    'bg-accent text-brand-teal-ink',
    'bg-brand-orange/15 text-brand-orange-ink'
]

const averageRating = TESTIMONIALS.reduce((total, item) => total + item.rating, 0) / TESTIMONIALS.length

const StoriesSection: FC = (): ReactNode => {
    return (
        <section id='stories' aria-labelledby='stories-title' className='py-20 sm:py-28'>
            <Container>
                <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        id='stories-title'
                        eyebrow="Success stories"
                        title="Hear it from our learners"
                        description="Job seekers who trained with Skillkafe share what it did for them."
                    />

                    <div className='flex shrink-0 items-center gap-4 self-start rounded-2xl border border-border bg-card px-5 py-4 lg:self-auto'>
                        <p className='font-heading text-4xl font-bold text-foreground'>
                            {averageRating.toFixed(1)}
                        </p>

                        <div>
                            <StarRating value={averageRating} />
                            <p className='mt-1 text-sm text-muted-foreground'>Average rating from our learners</p>
                        </div>
                    </div>
                </div>

                <ul className='mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4'>
                    {TESTIMONIALS.map((testimonial, index) => (
                        <li key={testimonial.name} className='reveal'>
                            <figure className='relative flex h-full flex-col rounded-[1.75rem] border border-border bg-card p-7'>
                                <Quote className='absolute right-6 top-6 size-8 text-secondary' fill='currentColor' strokeWidth={0} aria-hidden='true' />

                                <div className='flex items-center gap-2'>
                                    <StarRating value={testimonial.rating} />
                                    <span className='text-sm font-semibold text-foreground'>
                                        {testimonial.rating.toFixed(1)}
                                    </span>
                                </div>

                                <blockquote className='mt-5 flex-1 text-[17px] leading-relaxed text-foreground'>
                                    <p>
                                        &ldquo;{testimonial.quote}&rdquo;
                                    </p>
                                </blockquote>

                                <figcaption className='mt-6 flex items-center gap-3 border-t border-border pt-5'>
                                    <span
                                        aria-hidden='true'
                                        className={cn(
                                            'grid size-11 shrink-0 place-items-center rounded-full font-heading text-sm font-semibold',
                                            AVATAR_TONES[index % AVATAR_TONES.length]
                                        )}
                                    >
                                        {getInitials(testimonial.name)}
                                    </span>

                                    <span className='min-w-0'>
                                        <span className='block font-semibold leading-snug text-foreground'>
                                            {testimonial.name}
                                        </span>

                                        <span className='block text-sm text-muted-foreground'>
                                            {testimonial.label}
                                        </span>
                                    </span>
                                </figcaption>
                            </figure>
                        </li>
                    ))}
                </ul>
            </Container>
        </section>
    )
}

export default StoriesSection
